import type { Ref } from 'react';
import { type JSX, type TextareaHTMLAttributes, useEffect, useMemo, useRef } from 'react';
import type { Extension } from '@codemirror/state';
import { useMergeRefs } from '@gonfalon/react/useMergeRefs';
import { Label } from '@launchpad-ui/components';
import clsx from 'clsx';
import type CodeMirrorPackage from 'codemirror';
import type { JSONSchema7 } from 'json-schema';
// oxlint-disable-next-line no-restricted-imports -- TODO(zod-4): migrate to 'zod/v4'
import type { ZodSchema } from 'zod';

import type { ExtensionConfig } from './internal/CodeEditorAdvanced/extensions';
import type { SupportedMode } from './internal/CodeEditorAdvanced/modes/lazyModeLoader';
import type {
  CodeEditorHandle,
  SyntaxHighlightingOverrides,
  ThemeOverrides,
} from './internal/CodeEditorAdvanced/types';
import { LazyCodeEditorAdvanced } from './LazyCodeEditorAdvanced';

import './internal/styles.css';

// Legacy CodeEditor props interface for compatibility (from static/ld/components/ui/codeEditor/CodeEditor.tsx)
export type LegacyCodeEditorProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> & {
  value?: string;
  mode?: SupportedMode;
  lint?: boolean;
  autoFocus?: boolean;
  keyMap?: CodeMirror.KeyMap;
  onChange?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  className?: string;
  options?: {};
  readOnly?: boolean;
  resizable?: boolean;
  fullscreen?: boolean;
  // CodeMirror is passed in as a prop to allow for lazy-loading
  CodeMirror?: typeof CodeMirrorPackage;
  id?: string;
  name?: string;
  label?: string | JSX.Element | null;
  description?: string | JSX.Element | null;
  screenReaderLabel?: string;
  startCursorAtLine?: number;
  // Optionally bootstraps the editor with any custom modes/code that are specific to editor instances
  bootstrapFn?: (
    editor: CodeMirrorPackage.EditorFromTextArea,
    codeMirror: typeof CodeMirrorPackage,
  ) => CodeMirrorPackage.EditorFromTextArea;
  // If true, will swallow errors that occur when the component unmounts
  // These can trigger when combined with suspense fetches/loading states, and throw globally
  // so they don't get captured by error boundaries. Swallowing them stops the error from bubbling
  // but doesn't prevent the component from unmounting or being remounted.
  swallowUnmountErrors?: boolean;
  // If true, will disable the active line highlight, basically turn styleActiveLine false
  disableActiveLineHighlight?: boolean;
  extensionConfigs?: ExtensionConfig;
  extensions?: Extension[];
  themeOverrides?: ThemeOverrides;
  codeEditorHandle?: Ref<CodeEditorHandle | null>;
  syntaxHighlightingOverrides?: SyntaxHighlightingOverrides;
  innerRef?: Ref<unknown>;
  schema?: JSONSchema7;
  zodSchema?: ZodSchema;
};

const baseTheme = {
  '&': {
    fontSize: 'var(--lp-font-size-200)',
    lineHeight: 'var(--lp-line-height-200)',
  },
  '.cm-scroller': {
    lineHeight: 'var(--lp-line-height-200)',
  },
  '.cm-activeLineGutter': {
    backgroundColor: 'var(--lp-color-bg-feedback-info)',
  },
  '.cm-activeLine': {
    backgroundColor: 'var(--lp-color-bg-feedback-info)',
  },
};

/**
 * Compatibility wrapper that adapts legacy CodeEditor props to work with LazyCodeEditorAdvanced.
 * This allows gradual migration from the old CodeMirror 5-based editor to the new CodeMirror 6-based editor.
 */
export const LazyCodeEditorCompatWrapper = (props: LegacyCodeEditorProps) => {
  const {
    value = '',
    readOnly,
    id = 'editor',
    label = 'Editor',
    description,
    mode = 'json',
    className,
    extensionConfigs,
    extensions,
    screenReaderLabel,
    autoFocus,
    lint = false,
    startCursorAtLine,
    disableActiveLineHighlight,
    themeOverrides,
    // Legacy props that don't have direct equivalents - we'll ignore these for now
    keyMap,
    options,
    CodeMirror,
    bootstrapFn,
    swallowUnmountErrors,
    innerRef,
    codeEditorHandle,
    ...restProps
  } = props;
  const handleRef = useRef<CodeEditorHandle>(null);

  const handleRefs = useMergeRefs(handleRef, codeEditorHandle);

  // Build extension config based on legacy options
  const extensionConfig = useMemo<ExtensionConfig>(
    () => ({
      lineNumbers: false,
      foldGutter: false,
      history: false,
      highlightActiveLine: !disableActiveLineHighlight,
      bracketMatching: mode === 'json',
      closeBrackets: mode === 'json',
      autocompletion: false,
      rectangularSelection: false,
      crosshairCursor: false,
      highlightSelectionMatches: false,
      lint,
      ...extensionConfigs,
    }),
    [disableActiveLineHighlight, extensionConfigs, lint, mode],
  );

  const mergedThemeOverrides = useMemo<ThemeOverrides>(() => ({ ...baseTheme, ...themeOverrides }), [themeOverrides]);

  useEffect(() => {
    if (innerRef && handleRef.current) {
      if (typeof innerRef === 'function') {
        innerRef(handleRef.current.containerRef);
      } else {
        innerRef.current = handleRef.current.containerRef;
      }
    }
  }, [innerRef, id]);

  return (
    <div aria-label={screenReaderLabel} className={clsx('CodeEditor', className)}>
      {label && <Label htmlFor={id}>{label}</Label>}
      {description && <p className="CodeEditor-description">{description}</p>}

      <LazyCodeEditorAdvanced
        className="CodeMirror"
        id={id}
        docString={value}
        disabled={readOnly}
        extensionConfig={extensionConfig}
        extensions={extensions}
        mode={mode}
        initialState={{
          startCursorAtLine,
          autoFocus,
        }}
        fullscreen={false}
        themeOverrides={mergedThemeOverrides}
        codeEditorHandle={handleRefs}
        {...restProps}
      />
    </div>
  );
};
